import { SegmentFilter } from '../segmentFilters';
import { StatItemProps } from './SegmentItem';
import { SegmentedBar, SingleStat, StatRow, WIDGET_COLORS, WidgetCard, WidgetTooltip, registrationIcon } from './WidgetPrimitives';

// These segments count registrations rather than educators, so filtering by one shows
// the educators holding at least one registration with that status.
// Shared by the bar segment and the stat below it so both toggle the same filter.
const registrationFilter = (status: string, label: string): SegmentFilter => ({
  attribute: 'has_registration',
  value: status,
  group: 'Program Registrations',
  label
});

const registrationStat = (percent: number, status: string, label: string, color?: string): StatItemProps => ({
  value: `${percent}%`,
  label,
  color,
  filter: registrationFilter(status, label)
});

export interface ProgramRegistrationsWidgetData {
  total_count?: number;
  attended_staff_count?: number;
  counts?: {
    completed?: number;
    pending?: number;
    attended?: number;
    registered?: number;
  };
  percentages?: {
    completed?: number;
    pending?: number;
    attended?: number;
    registered?: number;
  };
}

interface ProgramRegistrationsWidgetProps {
  data: ProgramRegistrationsWidgetData;
}

const TOOLTIP = (
  <WidgetTooltip
    heading="Program Registrations"
    description={[
      'Shows progress through program registration, approval, attendance, and completion.',
      'The total count represents the number of registrations. Each educator may have multiple registrations throughout the year across different programs.'
    ]}
    definitions={[
      {
        term: 'Completed',
        description: 'Educators who have registrations marked as completed by an administrator.',
        color: WIDGET_COLORS.green
      },
      {
        term: 'Attended',
        description: 'Educators who have attended at least one workshop within a program.',
        color: WIDGET_COLORS.blue
      },
      {
        term: 'Registered',
        description: 'Educators who have approved registrations and are awaiting attendance.',
        color: WIDGET_COLORS.lightBlue
      },
      {
        term: 'Pending',
        description:
          'Educators who have submitted a registration and are awaiting supervisor approval. This step is skipped if approval is not required.',
        color: WIDGET_COLORS.yellow
      }
    ]}
  />
);

const ProgramRegistrationsWidget = ({
  data: {
    total_count: total = 0,
    attended_staff_count: attendedStaffCount = 0,
    counts: {
      completed: completedCount = 0,
      pending: pendingCount = 0,
      attended: attendedCount = 0,
      registered: registeredCount = 0
    } = {},
    percentages: {
      completed: completedPercent = 0,
      pending: pendingPercent = 0,
      attended: attendedPercent = 0,
      registered: registeredPercent = 0
    } = {},
  }
}: ProgramRegistrationsWidgetProps) => (
  <WidgetCard
    title="Program Registrations"
    total={total}
    unit="registrations"
    // "Total registrations" is too long for the header, so the unit stands alone.
    totalLabel="Registrations"
    icon={registrationIcon}
    tooltip={TOOLTIP}
  >
    <SegmentedBar
      unit="registrations"
      segments={[
        {
          percent: completedPercent,
          color: WIDGET_COLORS.green,
          label: 'Completed',
          count: completedCount,
          filter: registrationFilter('completed', 'Completed')
        },
        {
          percent: attendedPercent,
          color: WIDGET_COLORS.blue,
          label: 'Attended',
          count: attendedCount,
          filter: registrationFilter('attended', 'Attended')
        },
        {
          percent: registeredPercent,
          color: WIDGET_COLORS.lightBlue,
          label: 'Registered',
          count: registeredCount,
          filter: registrationFilter('registered', 'Registered')
        },
        {
          percent: pendingPercent,
          color: WIDGET_COLORS.yellow,
          label: 'Pending',
          count: pendingCount,
          filter: registrationFilter('pending', 'Pending')
        }
      ]}
    />
    <StatRow
      items={[
        registrationStat(completedPercent, 'completed', 'Completed', WIDGET_COLORS.green),
        registrationStat(attendedPercent, 'attended', 'Attended', WIDGET_COLORS.blue),
        registrationStat(registeredPercent, 'registered', 'Registered', WIDGET_COLORS.lightBlue),
        registrationStat(pendingPercent, 'pending', 'Pending', WIDGET_COLORS.yellow)
      ]}
    />
    <SingleStat value={`${attendedStaffCount}`} label="No. of staff attended at least 1 workshop" />
  </WidgetCard>
);

export default ProgramRegistrationsWidget;
